/* PlannerResponseHero — Doctor-side card that shows the planner's
 * decision on every item the doctor asked for in their most-recent
 * revision request.
 *
 * Rendered at the TOP of the Treatment Plan tab whenever there's an
 * `actioned` revision_request in the case history. Two states:
 *
 *   - decision === "accepted" → green "Addressed" pill + applied value
 *   - decision === "denied"   → rose "Declined" pill + planner rationale
 *
 * The hero is the doctor's single visual answer to "what did the
 * planner do with my last revision request?". Combined with the
 * compact "iteration N" banner above the case tabs and the History
 * tab next to Treatment Plan, the round-trip is fully self-contained
 * on the case detail page — no separate `/review-approval` page
 * needed.  (iter98.99bl)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    CheckCircle, XCircle, ArrowRight, ArrowsClockwise, ClipboardText,
    User as UserIcon, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { expandRevisionItems, latestRevision } from "@/lib/revisionSections";
import SectionCard from "@/components/case/SectionCard";


const RETAINER_LABEL = {
    vivera: "Clear (Vivera-style)",
    hawley: "Hawley",
    fixed_bonded: "Fixed bonded",
    dual: "Dual (clear + fixed)",
    other: "Other",
};

const fmtVal = (v, unit, planKey) => {
    if (v === undefined || v === null || v === "") return "—";
    if (planKey === "retainer_type") return RETAINER_LABEL[v] || v;
    return unit ? `${v} ${unit}`.trim() : String(v);
};

const fmtShortDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return ""; }
};


export default function PlannerResponseHero({ c, defaultOpen = false, onJumpToHistory }) {
    const [open, setOpen] = useState(defaultOpen);
    const [beforePlan, setBeforePlan] = useState(null);

    const revisions = c?.revision_requests || [];
    /* Most recent ACTIONED revision (the one the planner has responded
       to). If the doctor has since requested another revision and
       it's still open, we still show the previous response card so the
       audit trail is preserved. */
    const sortedActioned = useMemo(
        () => revisions
            .filter((r) => r.status === "actioned")
            .sort((a, b) => (b.iteration || 0) - (a.iteration || 0)),
        [revisions]
    );
    const rev = sortedActioned[0];

    /* Index decisions by row key for O(1) lookup. Must come BEFORE the
       early-return so the hook order is stable across renders. */
    const decisionByKey = useMemo(() => {
        const m = {};
        (rev?.decisions || []).forEach((d) => { m[d.key] = d; });
        return m;
    }, [rev]);

    /* Load the plan snapshot at the moment the revision was requested
       so the "from" column shows the value that was in the plan when
       the doctor pressed Request Revision — not the latest value. */
    useEffect(() => {
        if (!c?.id || !rev) return;
        let alive = true;
        api.get(`/cases/${c.id}/plan-versions`)
            .then((r) => {
                if (!alive) return;
                const versions = r.data || [];
                const revAt = rev.requested_at;
                const candidate = [...versions]
                    .filter((v) => v.reason === "revision_requested")
                    .filter((v) => !revAt || new Date(v.created_at) <= new Date(revAt).getTime() + 2000)
                    .sort((a, b) => (b.version || 0) - (a.version || 0))[0];
                setBeforePlan(candidate?.plan || null);
            })
            .catch(() => setBeforePlan(null));
        return () => { alive = false; };
    }, [c?.id, rev?.requested_at]);

    if (!rev) return null;

    const rows = expandRevisionItems(rev, beforePlan || c?.plan || {});

    /* iter98.99bl — Two-state pill: accepted=Addressed, denied=Declined.
       If the planner didn't record a decision (legacy `actioned_items`
       array only), fall back to that. */
    const acceptedKeys = new Set(rev.actioned_items || []);
    const decisionFor = (row) => {
        const d = decisionByKey[row.key];
        if (d?.decision === "accepted") return { kind: "accepted", rationale: d.rationale, appliedValue: d.applied_value };
        if (d?.decision === "denied")   return { kind: "denied",   rationale: d.rationale };
        if (acceptedKeys.has(row.key))  return { kind: "accepted" };
        return null;
    };

    const accepted = rows.filter((r) => decisionFor(r)?.kind === "accepted").length;
    const denied = rows.filter((r) => decisionFor(r)?.kind === "denied").length;
    const total = rows.length;

    const iterationLabel = `Iteration ${rev.iteration || sortedActioned.length}`;
    const plannerName = rev.actioned_by_name || "your planning team";

    return (
        <div
            className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.05] to-card overflow-hidden shadow-sm"
            data-testid="planner-response-hero"
        >
            {/* Header band — matches ApprovalSection chrome */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full bg-secondary/60 hover:bg-secondary px-4 py-3 border-b border-border/70 flex items-center gap-3 transition-colors text-left"
                aria-expanded={open}
                data-testid="planner-response-hero-toggle"
            >
                <span className="shrink-0 w-[3px] h-5 rounded-sm bg-primary" />
                <ArrowsClockwise size={15} weight="duotone" className="text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-semibold text-foreground truncate">
                        Planner&apos;s response to your last revision
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 truncate mt-0.5">
                        {iterationLabel} · resubmitted by {plannerName}{rev.actioned_at ? ` on ${fmtShortDate(rev.actioned_at)}` : ""} · {accepted} addressed{denied ? ` · ${denied} declined` : ""}
                    </div>
                </div>
                {/* Summary pills */}
                <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                    <Pill kind="accepted" compact>{accepted} addressed</Pill>
                    {denied > 0 && <Pill kind="denied" compact>{denied} declined</Pill>}
                </div>
                {open ? <CaretUp size={14} weight="bold" className="text-muted-foreground shrink-0" />
                      : <CaretDown size={14} weight="bold" className="text-muted-foreground shrink-0" />}
            </button>

            {open && (
                <div className="p-4 sm:p-5">
                    {/* Doctor's overall note (the "Overall notes" field on the request form)
                        iter98.99de — Wrapped in SectionCard so the rhythm
                        matches the Submission tab brief. */}
                    {rev.note && (
                        <SectionCard
                            icon={ClipboardText}
                            label="Your overall note"
                            testid="planner-response-overall-note"
                            className="mb-4"
                        >
                            <div className="text-[12.5px] text-foreground/85 italic leading-relaxed">
                                “{rev.note}”
                            </div>
                        </SectionCard>
                    )}

                    {/* Per-item table */}
                    {rows.length === 0 ? (
                        <div className="text-[12.5px] text-muted-foreground italic">
                            No structured items in this revision request.
                        </div>
                    ) : (
                        <div className="rounded-xl border border-border overflow-hidden" data-testid="planner-response-hero-table">
                            {/* Table header */}
                            <div className="hidden md:grid grid-cols-12 gap-3 bg-secondary/40 px-4 py-2 border-b border-border/70 text-[9.5px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                <div className="col-span-3">Item</div>
                                <div className="col-span-4">You asked</div>
                                <div className="col-span-3">Planner did</div>
                                <div className="col-span-2 text-right">Status</div>
                            </div>
                            <div className="divide-y divide-border">
                                {rows.map((row, i) => {
                                    const d = decisionFor(row);
                                    return (
                                        <ResponseRow
                                            key={row.key || i}
                                            row={row}
                                            decision={d}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Footer with link to full history */}
                    {onJumpToHistory && (
                        <button
                            type="button"
                            onClick={onJumpToHistory}
                            className="mt-3 text-[12px] text-primary hover:underline inline-flex items-center gap-1"
                            data-testid="planner-response-hero-history-link"
                        >
                            View full revision history ({total} item{total === 1 ? "" : "s"} this round)
                            <ArrowRight size={11} weight="bold" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}


function ResponseRow({ row, decision }) {
    const askText = row.kind === "struct"
        ? fmtVal(row.to, row.unit, row.planKey)
        : row.note;
    const fromText = row.kind === "struct" ? fmtVal(row.from, row.unit, row.planKey) : null;
    const responseText = (() => {
        if (!decision) return <span className="italic text-muted-foreground">No decision recorded</span>;
        if (decision.kind === "accepted") {
            if (row.kind === "struct") {
                const applied = decision.appliedValue ?? row.to;
                return <span className="font-semibold text-foreground">{fmtVal(applied, row.unit, row.planKey)}</span>;
            }
            return decision.rationale
                ? <span>{decision.rationale}</span>
                : <span className="italic text-muted-foreground">Applied to plan</span>;
        }
        // denied
        return decision.rationale
            ? <span><strong className="font-semibold">Declined.</strong> {decision.rationale}</span>
            : <span className="italic text-muted-foreground">Declined (no rationale).</span>;
    })();

    return (
        <div
            className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 py-3 text-[13px]"
            data-testid={`planner-response-row-${row.key}`}
        >
            <div className="md:col-span-3 font-semibold text-foreground">
                {row.label}
                {row.kind === "free" && row.severity === "critical" && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded-full">Critical</span>
                )}
            </div>
            <div className="md:col-span-4 text-foreground/80">
                {row.kind === "struct" ? (
                    <span className="tabular-nums">
                        <span className="text-muted-foreground line-through opacity-70">{fromText}</span>
                        <ArrowRight size={11} weight="bold" className="inline mx-1.5 align-middle text-amber-600" />
                        <span className="text-foreground font-medium">{askText}</span>
                    </span>
                ) : (
                    <span className="italic">“{askText}”</span>
                )}
            </div>
            <div className="md:col-span-3 text-foreground/85">
                {responseText}
            </div>
            <div className="md:col-span-2 md:text-right">
                <Pill kind={decision?.kind || "unknown"} />
            </div>
        </div>
    );
}


function Pill({ kind, compact = false, children }) {
    const styles = {
        accepted: { Icon: CheckCircle, label: "Addressed", cls: "bg-primary/10 text-primary border-primary/30" },
        denied:   { Icon: XCircle,     label: "Declined",  cls: "bg-rose-100 text-rose-800 border-rose-200" },
        unknown:  { Icon: ClipboardText, label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    }[kind] || { Icon: ClipboardText, label: kind, cls: "bg-secondary text-muted-foreground border-border" };
    const Icon = styles.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-[0.12em] ${styles.cls} ${compact ? "text-[9.5px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"}`}
            data-testid={`planner-response-pill-${kind}`}
        >
            <Icon size={compact ? 9 : 11} weight="fill" />
            {children || styles.label}
        </span>
    );
}
